import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Mail, Copy, ExternalLink } from "lucide-react";
import { generateProposalPDF } from "@/lib/pdfGenerator";
import { factSheetNumbers } from "@/lib/proposalData";
import LettersOfSupport from "@/components/site/LettersOfSupport";
import { toast } from "sonner";

const FOUNDER_IMG = "/assets/george-broughton.jpg";
const FOUNDER_IMG_2 = "/assets/george-broughton-2.jpg";

const boilerplates = {
  short:
    "The Baytown Bridge is a multigenerational service connector initiative serving single-mother households and aging-in-place seniors in Baytown, Texas. Founded by George Broughton III — who managed a 105-family service connector caseload at The Woodlawn Organization / WCDC in Chicago from 2001 to 2007 and now leads Sterile Mate LLC out of Houston — Bridge provides high-touch, multi-year case management that walks residents through Lee College, Harris Health, the Baytown Housing Authority, and Workforce Solutions until they reach a thriving baseline.",
  medium:
    "The Baytown Bridge is a localized adaptation of the federally-recognized Service Connector model. Operating in Baytown, Texas — a 86,004-resident, majority-Hispanic community where 77.6% of residents report rising housing costs — Bridge serves as the permanent 'air traffic controller' for two parallel cohorts: single-mother households on a path through Lee College, and seniors aging in place. The model is a direct CDBG public-service fit aligned with all five objectives of the City of Baytown's 2025–2029 Consolidated Plan, and is led by George Broughton III, who managed a 105-family service-connector caseload at The Woodlawn Organization / WCDC in Chicago (2001–2007) and is CEO of Sterile Mate LLC.",
};

const founderBio =
  "George Broughton III is the Program Creator of the Baytown Bridge — a multigenerational service connector initiative for Baytown and Harris County. From April 2001 to May 2007, George served as a Case Manager Service Connector at The Woodlawn Organization / WCDC in Chicago, managing a continuous caseload of 105 families through employment, education, healthcare, and housing pipelines. He is Founder & CEO of Sterile Mate LLC, a Houston medical-supply company he built during the COVID-19 pandemic to serve the Texas Medical Center. He brings that battle-tested operational framework — and entrepreneurial DNA — to Baytown, scaled for the dual-county landscape of Harris and Chambers.";

const factSheet = factSheetNumbers;

const mediaContact = {
  name: "George Broughton",
  title: "Program Creator & Founding Architect, Baytown Bridge",
  email: "george.broughton@sterilemate.com",
};

export default function PressKitPage() {
  const copy = async (text, label) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for insecure contexts / older browsers
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`${label} copied to clipboard.`);
    } catch (err) {
      toast.error("Could not copy automatically. Please select the text and copy manually.");
    }
  };

  return (
    <main data-testid="press-page" className="bg-[#F9F7F1] min-h-screen">
      <div className="bg-[#0A192F] text-[#F9F7F1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="press-back-link"
            className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-[#F9F7F1] flex items-center gap-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Baytown Bridge
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#CBD5E1]">
            Press & Funder Kit
          </span>
        </div>
      </div>

      {/* Header */}
      <section className="bg-white border-b border-[#CBD5E1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                For Press, Funders & Partners
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0A192F] leading-tight">
              Baytown Bridge<br />
              <em className="italic">Press & Funder Kit</em>
            </h1>
            <p className="mt-6 text-[#334155] leading-relaxed max-w-2xl">
              Boilerplates, founder bio, headshots, fact sheet, downloadable proposal,
              and direct media contact. Everything you need to write about, fund, or
              partner with the Baytown Bridge initiative.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="press-download-proposal"
                onClick={generateProposalPDF}
                className="rounded-none bg-[#0A192F] hover:bg-[#15243F] text-[#F9F7F1] h-12 px-6 font-mono text-xs uppercase tracking-widest"
              >
                <Download className="h-4 w-4 mr-2" /> Full Proposal PDF
              </Button>
              <Button
                data-testid="press-email-george"
                asChild
                variant="outline"
                className="rounded-none border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-[#F9F7F1] h-12 px-6 font-mono text-xs uppercase tracking-widest"
              >
                <a href={`mailto:${mediaContact.email}`}>
                  <Mail className="h-4 w-4 mr-2" /> Contact George
                </a>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={FOUNDER_IMG}
              alt="George Broughton, founder"
              className="w-full aspect-[4/5] object-cover object-center border border-[#CBD5E1]"
            />
          </div>
        </div>
      </section>

      {/* Boilerplates */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#8B0000]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
              01 — Boilerplates
            </span>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <BoilerCard
              testId="boiler-short"
              tag="Short · 60 words"
              text={boilerplates.short}
              onCopy={() => copy(boilerplates.short, "Short boilerplate")}
            />
            <BoilerCard
              testId="boiler-medium"
              tag="Medium · 130 words"
              text={boilerplates.medium}
              onCopy={() => copy(boilerplates.medium, "Medium boilerplate")}
            />
          </div>
        </div>
      </section>

      {/* Founder bio */}
      <section className="bg-white border-y border-[#CBD5E1] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#8B0000]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
              02 — Founder Bio
            </span>
          </div>
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <h2 className="font-display text-3xl lg:text-4xl text-[#0A192F] leading-tight">
                George Broughton
              </h2>
              <p className="mt-4 text-[#334155] leading-relaxed">{founderBio}</p>
              <Button
                data-testid="bio-copy-btn"
                onClick={() => copy(founderBio, "Founder bio")}
                variant="outline"
                className="mt-6 rounded-none border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-[#F9F7F1] h-11 px-5 font-mono text-xs uppercase tracking-widest"
              >
                <Copy className="h-3.5 w-3.5 mr-2" /> Copy bio
              </Button>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              <PhotoCard testId="headshot-1" src={FOUNDER_IMG} label="Headshot · Seated portrait" />
              <PhotoCard testId="headshot-2" src={FOUNDER_IMG_2} label="Headshot · Full-length" />
            </div>
          </div>
        </div>
      </section>

      {/* Fact sheet */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#8B0000]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
              03 — Fact Sheet (Verified)
            </span>
          </div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A192F] leading-tight mb-8">
            The numbers, fact-checked.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            {factSheet.map((s, i) => (
              <div
                key={i}
                data-testid={`factsheet-${i}`}
                className="bg-white p-6"
              >
                <div className="font-display text-3xl text-[#0A192F]">{s.v}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-2 leading-snug">
                  {s.k}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#64748B]">
            Sources — U.S. Census QuickFacts (Baytown city, TX, 2024) + City of Baytown
            2025–2029 Consolidated Plan Survey (n=98).
          </p>
        </div>
      </section>

      {/* Contact + Links */}
      <LettersOfSupport />

      <section className="bg-[#0A192F] text-[#F9F7F1] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">
              04 — Media Contact
            </div>
            <div className="font-display text-3xl lg:text-4xl">{mediaContact.name}</div>
            <div className="text-[#CBD5E1] mt-2">{mediaContact.title}</div>
            <a
              href={`mailto:${mediaContact.email}`}
              data-testid="press-media-email"
              className="inline-flex items-center gap-2 mt-6 font-mono text-sm text-[#D4AF37] hover:text-[#F9F7F1]"
            >
              <Mail className="h-4 w-4" />
              {mediaContact.email}
            </a>
          </div>
          <div className="lg:col-span-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">
              05 — Useful Links
            </div>
            <div className="space-y-3">
              <LinkRow to="/" label="Full Proposal Site" external={false} testId="press-link-home" />
              <LinkRow to="/pitch" label="Council Member Pitch-Letter Generator" external={false} testId="press-link-pitch" />
              <LinkRow
                href="https://www.census.gov/quickfacts/fact/table/baytowncitytexas/PST045224"
                label="U.S. Census QuickFacts — Baytown, TX"
                external
                testId="press-link-census"
              />
              <LinkRow
                href="https://baytown.org/Archive.aspx?ADID=351"
                label="City of Baytown CDBG Page"
                external
                testId="press-link-cdbg"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function BoilerCard({ testId, tag, text, onCopy }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#CBD5E1] p-6 lg:p-7 flex flex-col">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-3">{tag}</div>
      <p className="text-[#334155] leading-relaxed text-sm flex-1">{text}</p>
      <Button
        onClick={onCopy}
        variant="outline"
        className="mt-5 self-start rounded-none border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-[#F9F7F1] h-10 px-4 font-mono text-xs uppercase tracking-widest"
      >
        <Copy className="h-3.5 w-3.5 mr-2" /> Copy
      </Button>
    </div>
  );
}

function PhotoCard({ src, label, testId }) {
  return (
    <a href={src} download data-testid={testId} className="block group">
      <div className="aspect-[4/5] overflow-hidden border border-[#CBD5E1]">
        <img src={src} alt={label} className="w-full h-full object-cover object-center transition-transform group-hover:scale-105" />
      </div>
      <div className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#475569] group-hover:text-[#8B0000]">
        <Download className="h-3 w-3" /> {label}
      </div>
    </a>
  );
}

function LinkRow({ to, href, label, external, testId }) {
  const inner = (
    <>
      <span>{label}</span>
      <ExternalLink className={`h-3.5 w-3.5 transition-transform ${external ? "" : "-rotate-45"}`} />
    </>
  );
  const baseCls =
    "flex items-center justify-between border border-[#F9F7F1]/15 p-4 hover:border-[#D4AF37] hover:text-[#D4AF37] text-sm transition-colors";
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={baseCls}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={baseCls}>
      {inner}
    </Link>
  );
}
